import {
  Box,
  VStack,
  Icon,
  Text,
  Flex,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useBreakpointValue,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBolt,
  FaBox,
  FaGift,
  FaStar,
  FaUsers,
  FaFileInvoiceDollar,
  FaUserCircle,
  FaCog,
  FaSignOutAlt,
  FaChevronDown,
  FaQrcode,
  FaExchangeAlt,
  FaUsersCog,
} from 'react-icons/fa';
import { useBusinessLogout } from '@/lib/sdk/accounts/queries';
import { useQueryClient } from '@tanstack/react-query';
import { clearStoredAuth, getLinkedAccounts } from '@/lib/auth';
import { useFeatureAccess } from '@/hooks';
import type { FeatureName } from '@/lib/sdk/accounts/types';

interface AdminSidebarProps {
  isOpen: boolean;
  isMobileView: boolean;
  onClose: () => void;
}

interface MenuItem {
  icon: any;
  label: string;
  path: string;
  feature?: FeatureName;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    title: 'Memberships',
    items: [
      { icon: FaBox, label: 'Clubs', path: '/admin/collections', feature: 'clubs' },
      { icon: FaGift, label: 'Offers', path: '/admin/airdrops', feature: 'offers' },
      { icon: FaStar, label: 'Privileges', path: '/admin/privileges', feature: 'privileges' },
      { icon: FaQrcode, label: 'Brand QR Codes', path: '/admin/qrcodes', feature: 'checkin_qr' },
    ],
  },
  {
    title: 'Users',
    items: [
      { icon: FaUsers, label: 'Members', path: '/admin/members', feature: 'members' },
    ],
  },
  {
    title: 'Billing',
    items: [
      { icon: FaFileInvoiceDollar, label: 'Invoices', path: '/admin/billing', feature: 'invoices' },
      { icon: FaFileInvoiceDollar, label: 'Offline Payments', path: '/admin/offline', feature: 'offline' },
      { icon: FaFileInvoiceDollar, label: 'Subscriptions', path: '/admin/subscription', feature: 'subscriptions' },
    ],
  },
];

interface MenuSectionProps {
  section: MenuSection;
  isMobile?: boolean;
  onItemClick?: () => void;
}

const MenuSectionComponent = ({ section, isMobile, onItemClick }: MenuSectionProps) => {
  const location = useLocation();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: !isMobile });
  const { canAccess } = useFeatureAccess();

  const visibleItems = section.items.filter((item) => {
    if (!item.feature) return true;
    return canAccess(item.feature);
  });

  if (visibleItems.length === 0) return null;

  return (
    <Box>
      <Button
        w="full"
        variant="ghost"
        justifyContent="space-between"
        onClick={onToggle}
        py={2}
        px={6}
        fontSize="sm"
        fontWeight="medium"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="wider"
        _hover={{ bg: 'whiteAlpha.100' }}
      >
        {section.title}
        <Icon
          as={FaChevronDown}
          transition="transform 0.2s"
          transform={isOpen ? 'rotate(-180deg)' : undefined}
        />
      </Button>
      <Collapse in={isOpen}>
        <VStack spacing={1} align="stretch">
          {visibleItems.map((item) => (
            <Box
              key={item.path}
              as={Link}
              to={item.path}
              px={6}
              py={3}
              color={location.pathname === item.path ? 'purple.400' : 'gray.400'}
              bg={location.pathname === item.path ? 'whiteAlpha.100' : 'transparent'}
              _hover={{
                bg: 'whiteAlpha.100',
                color: 'purple.400',
              }}
              transition="all 0.2s"
              onClick={onItemClick}
            >
              <Flex align="center" gap={3}>
                <Icon as={item.icon} boxSize={5} />
                <Text fontWeight="medium">{item.label}</Text>
              </Flex>
            </Box>
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
};

const SidebarContent = ({ isMobile, onItemClick }: { isMobile?: boolean; onItemClick?: () => void }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logoutMutation = useBusinessLogout();
  const hasMultipleAccounts = getLinkedAccounts().length > 0;

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Always clear auth and cache, even if the API call fails
      clearStoredAuth();
      queryClient.clear();
      navigate('/admin/login');
    }
  };

  return (
    <VStack spacing={8} align="stretch" py={6} h="full">
      {/* Logo */}
      <Box px={6}>
        <Link to="/admin" onClick={onItemClick}>
          <Flex align="center" gap={3}>
            <Icon as={FaBolt} boxSize={8} color="purple.400" />
            <Text
              fontSize="2xl"
              fontWeight="bold"
              bgGradient="linear(to-r, purple.400, pink.400)"
              bgClip="text"
            >
              Zeno
            </Text>
          </Flex>
        </Link>
      </Box>

      {/* Menu Sections */}
      <VStack spacing={6} align="stretch" flex={1}>
        {menuSections.map((section) => (
          <MenuSectionComponent
            key={section.title}
            section={section}
            isMobile={isMobile}
            onItemClick={onItemClick}
          />
        ))}
      </VStack>

      {/* User Menu */}
      <Box px={4}>
        <Divider borderColor="whiteAlpha.200" mb={4} />
        <Menu>
          <MenuButton
            as={Button}
            w="full"
            variant="ghost"
            leftIcon={<FaUserCircle />}
            rightIcon={<FaChevronDown />}
            justifyContent="space-between"
            color="gray.300"
            _hover={{ bg: 'whiteAlpha.200' }}
            _active={{ bg: 'whiteAlpha.300' }}
          >
            Admin
          </MenuButton>
          <MenuList>
            <MenuItem
              icon={<FaUserCircle />}
              onClick={() => {
                navigate('/admin/brand');
                onItemClick?.();
              }}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Brand Profile
            </MenuItem>
            <MenuItem
              icon={<FaUsersCog />}
              onClick={() => {
                navigate('/admin/team');
                onItemClick?.();
              }}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Team
            </MenuItem>
            <MenuItem
              icon={<FaCog />}
              onClick={() => {
                navigate('/admin/settings');
                onItemClick?.();
              }}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Settings
            </MenuItem>
            {hasMultipleAccounts && (
              <MenuItem
                icon={<FaExchangeAlt />}
                onClick={() => {
                  navigate('/admin/switch-account');
                  onItemClick?.();
                }}
                _hover={{ bg: 'whiteAlpha.200' }}
              >
                Switch Brand
              </MenuItem>
            )}
            <MenuItem
              icon={<FaSignOutAlt />}
              onClick={handleLogout}
              isDisabled={logoutMutation.isPending}
              color="red.400"
              _hover={{
                bg: 'red.900',
                color: 'white',
              }}
            >
              {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </VStack>
  );
};

const AdminSidebar = ({ isOpen, isMobileView, onClose }: AdminSidebarProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  if (isMobileView || isMobile) {
    return (
      <Drawer 
        isOpen={isOpen} 
        placement="left" 
        onClose={onClose}
        autoFocus={false}
      >
        <DrawerOverlay />
        <DrawerContent 
          bg="gray.900" 
          borderRight="1px" 
          borderColor="whiteAlpha.200"
        >
          <DrawerCloseButton 
            color="gray.400"
            _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
          />
          <DrawerBody p={0}>
            <SidebarContent isMobile={true} onItemClick={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Box
      w="280px"
      bg="whiteAlpha.50"
      borderRight="1px"
      borderColor="whiteAlpha.200"
      display={{ base: 'none', md: 'block' }}
      position="sticky"
      top={0}
      h="100vh"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '2px',
        },
      }}
    >
      <SidebarContent />
    </Box>
  );
};

export default AdminSidebar;